import { QuoteIcon } from "lucide-react";
import Image from "next/image";

import { Knit } from "@/components/logos/knit";
import { Maze } from "@/components/logos/maze";
import { Spade } from "@/components/logos/spade";
import { Vetto } from "@/components/logos/vetto";
import { cn } from "@/lib/utils";

import { Container } from "./ui/container";

const CARL_AVATAR = "/testimonials/carl-lippert.jpg";
const DANIEL_AVATAR = "/testimonials/daniel.jpg";
const GUILLAUME_AVATAR = "/testimonials/guillaume.jpg";
const DHANANJAY_AVATAR = "/testimonials/dhananjay.jpg";

interface Testimonial {
  quote: string;
  name: string;
  title: string;
  company: string;
  avatar?: string;
  logo?: React.ComponentType<{ className?: string }>;
}

const MAZE_TESTIMONIAL: Testimonial = {
  quote:
    "Terac's AI-led screening and ability to source fresh participants in days helps us reach niche audiences that we struggled to reach previously.",
  name: "Guillaume Polge",
  title: "Director of Panel Partnerships",
  company: "Maze",
  avatar: GUILLAUME_AVATAR,
  logo: Maze,
};

const testimonials: Testimonial[] = [
  MAZE_TESTIMONIAL,
  {
    quote:
      "The Terac team is responsive, proactive, and genuinely invested in making partnerships successful. They've demonstrated a clear focus on delivering value.",
    name: "DJ",
    title: "Fieldwork Manager",
    company: "Knit",
    avatar: DHANANJAY_AVATAR,
    logo: Knit,
  },
  {
    quote:
      "Terac helped us quickly find experts across multiple domains of expertise, enabling our projects to move forward efficiently. They are fast, flexible, and easy to work with, adapting well to changing needs and priorities.",
    name: "Daniel Rodrigues",
    title: "CHRO",
    company: "Vetto",
    avatar: DANIEL_AVATAR,
    logo: Vetto,
  },
  {
    quote:
      "Terac rapidly connected us with experienced dairy veterinarians to analyze our cow video and sensor data. This expertise is typically difficult to source. With Terac's support, our data was annotated in days rather than months, enabling us to deliver better technology to American dairy producers faster, ultimately contributing to a more resilient national food supply.",
    name: "Carl Lippert",
    title: "CTO",
    company: "Spade",
    avatar: CARL_AVATAR,
    logo: Spade,
  },
];

function Attribution({
  testimonial,
  compact,
}: {
  testimonial: Testimonial;
  compact?: boolean;
}) {
  const avatarSize = compact ? "size-10" : "size-12";
  return (
    <div className="mt-6 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        {testimonial.avatar ? (
          <Image
            src={testimonial.avatar}
            alt={`${testimonial.name}'s avatar`}
            width={96}
            height={96}
            className={`${avatarSize} rounded-full object-cover`}
          />
        ) : (
          <div
            className={`bg-muted text-muted-foreground flex ${avatarSize} items-center justify-center rounded-full font-medium`}
          >
            {testimonial.name.charAt(0)}
          </div>
        )}
        <div>
          <p className="text-foreground text-sm font-medium">
            {testimonial.name}
          </p>
          <p className="text-muted-foreground text-sm">
            {testimonial.title}, {testimonial.company}
          </p>
        </div>
      </div>
      {testimonial.logo && (
        <testimonial.logo
          className={`${compact ? "h-4" : "h-5"} shrink-0 opacity-60`}
        />
      )}
    </div>
  );
}

export default function TestimonialsSection({
  featuredCompany = "Maze",
}: {
  featuredCompany?: "Maze" | "Knit" | "Vetto" | "Spade";
}) {
  const featured =
    testimonials.find(
      (testimonial) => testimonial.company === featuredCompany,
    ) ?? MAZE_TESTIMONIAL;
  const rest = testimonials.filter((testimonial) => testimonial !== featured);

  return (
    <section className="py-20">
      <Container>
        <div className="mx-auto max-w-5xl">
          <div className="mb-12 text-center">
            <p className="text-muted-foreground text-sm font-medium tracking-wider uppercase">
              Trusted by innovative teams
            </p>
          </div>

          <div className="border-border relative border-l-2 pl-8">
            <QuoteIcon className="fill-background text-muted-foreground/50 absolute top-0 -left-3 size-6" />
            <blockquote className="text-foreground text-xl md:text-2xl">
              {featured.quote}
            </blockquote>
            <Attribution testimonial={featured} />
          </div>

          <div className="mt-14 grid gap-x-10 gap-y-12 md:grid-cols-2">
            {rest.map((testimonial, index) => (
              <div
                key={testimonial.name}
                className={cn(
                  "border-border flex flex-col justify-between border-l-2 pl-6",
                  // A final quote left alone on its row stretches across the empty
                  // cell rather than sitting beside a gap.
                  index === rest.length - 1 &&
                    rest.length % 2 === 1 &&
                    "md:col-span-2",
                )}
              >
                <blockquote className="text-foreground text-base">
                  {testimonial.quote}
                </blockquote>
                <Attribution testimonial={testimonial} compact />
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}
